import { formatRupeeLabel } from '../../../../../display-values/lumpsumPlanAmount'
import type { LumpsumRebalanceExecutedRecommendation } from '../../../../api/rebalance.types'
import { LumpsumRecommendationActionType } from '../../../../api/common.types'
import { PlanCategoryBucketKey } from '../../../../api/types'
import { useOpenPlanRecommendationBottomSheet } from '../../../../plan-bottom-sheet/open/useOpenPlanRecommendationBottomSheet'
import { AllocationFundCardTrailingLayout } from '../../../lumpsum-ui-components/AllocationFundCardTrailingLayout'
import { PlansFundCard, type PlanFundCardCta, type PlanFundCardInsight } from '../../../lumpsum-ui-components/PlansFundCard'
import { ALLOCATION_EXECUTED_FUND_CARD_COMPLETED_TICK_ICON_SVG } from '../../allocation/cards/AllocationExecutedFundCard'
import { buildRebalanceBuyInsights } from './RebalanceBuyFundCard'
import { buildRebalanceSellInsights } from './RebalanceSellFundCard'
import { PlansFundRole, PlansScreen } from '../../../../../analytics/plansEvents'
import { trackPlansFundCardCta, trackPlansFundCardOpen } from '../../../../../analytics/plansTrack'

type RebalanceExecutedFundCardProps = {
  recommendation: LumpsumRebalanceExecutedRecommendation
  showBottomDivider?: boolean
}

export function RebalanceExecutedFundCard({
  recommendation,
  showBottomDivider = true
}: RebalanceExecutedFundCardProps) {
  const { openPlanRecommendationBottomSheet } = useOpenPlanRecommendationBottomSheet()
  const isin = recommendation?.isin ?? ''
  const fundName = recommendation?.fundName ?? ''
  const screenName = PlansScreen.LumpsumRebalance
  const fundRole = PlansFundRole.Executed
  const currentValue = recommendation?.currentValue ?? 0
  const actionType = recommendation?.actionType ?? null
  const isBuy = actionType === LumpsumRecommendationActionType.BUY
  const actionLabel = isBuy ? 'Buy' : 'Sell'
  const completedLabel = `${actionLabel} completed`

  const openPlanBottomSheet = () => {
    openPlanRecommendationBottomSheet({
      isin,
      bucketType: PlanCategoryBucketKey.executedRecommendations,
      recommendationAction: actionType ?? undefined
    })
  }

  const handleCardClick = () => {
    trackPlansFundCardOpen({ screenName, fundRole, fundName, fundIsin: isin })
    openPlanBottomSheet()
  }

  const ctas: PlanFundCardCta[] = [
    {
      id: `${isin}-action`,
      label: completedLabel,
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: completedLabel, fundName, fundIsin: isin })
        console.log(completedLabel, isin)
      },
      variant: 'completed'
    },
    {
      id: `${isin}-view-plan`,
      label: 'View plan',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'View plan', fundName, fundIsin: isin })
        openPlanBottomSheet()
      },
      variant: 'tertiary'
    }
  ]

  return (
    <PlansFundCard
      fund={{
        isin,
        logoUrl: recommendation?.mfLogoUrl ?? '',
        title: fundName,
        fundNature: recommendation?.fundNature ?? '',
        category: recommendation?.fundCategory ?? '',
        purchaseMode: recommendation?.purchaseMode ?? ''
      }}
      topRight={<RebalanceExecutedFundCardTrailing currentValue={currentValue} />}
      status={{
        rank: recommendation?.powerRank ?? 0,
        rating: recommendation?.powerRating ?? ''
      }}
      ctas={ctas}
      insights={buildRebalanceExecutedInsights(recommendation)}
      showBottomDivider={showBottomDivider}
      onCardClick={handleCardClick}
    />
  )
}

function buildRebalanceExecutedInsights(
  recommendation: LumpsumRebalanceExecutedRecommendation
): PlanFundCardInsight[] {
  if (recommendation?.actionType === LumpsumRecommendationActionType.BUY) {
    return buildRebalanceBuyInsights(recommendation)
  }

  return buildRebalanceSellInsights(recommendation)
}

export function RebalanceExecutedFundCardTrailing({ currentValue }: { currentValue: number }) {
  return (
    <AllocationFundCardTrailingLayout
      primaryLabel={`${formatRupeeLabel({ rawAmount: currentValue })}`}
      secondaryIconHtml={ALLOCATION_EXECUTED_FUND_CARD_COMPLETED_TICK_ICON_SVG}
      secondaryText='Completed'
    />
  )
}
